import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  MeetingDealLinkDto,
  MeetingDto,
  MeetingLinkDto,
} from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingInsightsDto,
  MeetingSuggestedLinkDto,
  MeetingSuggestedLinkKind,
} from '@interfaces/meetings/responses/meeting-insights.response';
import {
  motivoDoNaoAchei,
  ouvidoComo,
  rotuloDaConfianca,
  rotuloDoTipoSugerido,
  sugestoesVisiveis,
} from '@features/dashboard/meetings/pages/meeting-detail/meeting-insights-state';
import {
  PARTNER_DEAL_STAGE_LABELS,
  PartnerDealStage,
} from '@interfaces/partners/responses/partner-deal.response';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  rotuloDoDealDoPipeline,
  rotuloDoDealVinculado,
  rotuloDoVinculo,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { MeetingEvidenceComponent } from '@features/dashboard/meetings/pages/meeting-detail/components/meeting-evidence/meeting-evidence.component';

/**
 * Aba **Vínculos** do detalhe: o "Vincular" no topo (vale para tudo o que a modal cobre: empresas,
 * inclusive as associadas, parceiro, deal, relacionamento e contato bancário) e os vínculos agrupados
 * por tipo, cada grupo com o seu vazio: Empresas, Parceiros, Deals (automático por e-mail ou manual),
 * Relacionamentos bancários, Contatos bancários e Pipeline de crédito (deal do kanban de crédito, Adendo 2
 * do PBI 3225, com link para abrir o card). Depois, numa seção própria, o que o agente de vínculos do Cog achou
 * na conversa (PBI 3225, task 3227), cada um com a confiança e o trecho da fala que o sustenta, mais
 * o texto do agente explicando o que achou e o que não achou.
 *
 * Sugestão do Cog é confirmada (`confirmarSugestao`) ou dispensada (`descartarSugestao`) com um
 * clique, sem modal; a aplicada vira vínculo com o selo "Cog" no grupo do tipo. A seção só aparece
 * para o time interno: o parceiro não vê sugestões nem a explicação do agente.
 */
@Component({
  selector: 'app-meeting-links-panel',
  standalone: true,
  imports: [SharedModule, RouterLink, NgTemplateOutlet, MeetingEvidenceComponent],
  templateUrl: './meeting-links-panel.component.html',
  styleUrl: './meeting-links-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingLinksPanelComponent {
  readonly meeting = input.required<MeetingDto>();
  readonly insights = input<MeetingInsightsDto | null>(null);
  /** Libera vincular e desvincular (time interno pela visão geral). */
  readonly interno = input(false);
  readonly ocupada = input(false);
  readonly temVideo = input(false);
  /** Id da sugestão sendo aplicada ou dispensada. */
  readonly decidindo = input<string | null>(null);

  readonly vincular = output<void>();
  readonly desvincularDeal = output<MeetingDealLinkDto>();
  readonly desvincularVinculo = output<MeetingLinkDto>();
  readonly confirmarSugestao = output<MeetingSuggestedLinkDto>();
  readonly descartarSugestao = output<MeetingSuggestedLinkDto>();
  readonly irPara = output<number>();

  /**
   * Vínculos separados por tipo, na ordem da tela. A empresa só citada (registrada pelo Cog, não abre a
   * reunião para o time) tem grupo próprio e não conta como vínculo de assunto.
   */
  protected readonly grupos = computed(() => {
    const links = this.meeting().links ?? [];
    const assunto = links.filter(l => l.role !== 'Mentioned');
    const doTipo = (kind: MeetingLinkDto['kind']) => assunto.filter(l => l.kind === kind);
    return {
      empresas: doTipo('Company'),
      citadas: links.filter(l => l.role === 'Mentioned' && l.kind === 'Company'),
      parceiros: doTipo('Partner'),
      relacionamentos: doTipo('BankRelationship'),
      contatos: doTipo('BankContact'),
      pipeline: doTipo('PipelineDeal'),
    };
  });

  /** Quantos vínculos de assunto a reunião tem (manuais e deals); a empresa só citada não entra. */
  protected readonly totalDeVinculos = computed(
    () =>
      (this.interno()
        ? (this.meeting().links ?? []).filter(l => l.role !== 'Mentioned').length
        : 0) + (this.meeting().deals?.length ?? 0)
  );

  /**
   * O "não vinculei" do Cog em dois grupos, para a tela não dizer "não encontrei" do que ele achou e só não
   * vinculou: sem cadastro (ou com dois cadastros possíveis) de um lado; citado de passagem ou fora do
   * escopo do outro.
   */
  protected readonly naoVinculados = computed(() => {
    const itens = this.insights()?.linksNotFound ?? [];
    return {
      semCadastro: itens.filter(i => i.reason === 'NoMatch' || i.reason === 'Ambiguous'),
      soCitados: itens.filter(i => i.reason === 'PassingMention' || i.reason === 'OutOfScope'),
    };
  });

  protected readonly rotuloDoDeal = rotuloDoDealVinculado;
  protected readonly rotuloDoVinculo = rotuloDoVinculo;

  /** O card do deal abre no kanban do Pipeline de crédito pelo `?dealId=`. */
  protected readonly rotaDoPipeline = '/dashboard/negotiation/credit-pipeline/kanban';

  /** "#1234 · Referência", sem a empresa (o grupo já é do pipeline). */
  protected rotuloDoDealDoPipeline(link: MeetingLinkDto): string {
    return rotuloDoDealDoPipeline({
      sequentialId: link.pipelineDealSequentialId ?? null,
      reference: link.pipelineDealReference ?? null,
    });
  }

  /** Sugestões a decidir primeiro (maior confiança antes), depois as aplicadas. Dispensadas somem. */
  protected readonly sugestoes = computed(() => sugestoesVisiveis(this.insights()?.suggestedLinks));

  /** Situação do agente de vínculos (sem insights = nada pedido). */
  protected readonly situacaoDoCog = computed(() => this.insights()?.linksStatus ?? 'NotStarted');

  /** A explicação do agente começa fechada: é para quem quer saber o porquê. */
  protected readonly explicacaoAberta = signal(false);

  protected readonly ouvidoComo = ouvidoComo;
  protected readonly motivoDoNaoAchei = motivoDoNaoAchei;

  protected tipoSugerido(kind: MeetingSuggestedLinkKind): string {
    return rotuloDoTipoSugerido(kind);
  }

  protected rotuloDaEtapa(stage: string): string {
    return PARTNER_DEAL_STAGE_LABELS[stage as PartnerDealStage] ?? stage;
  }

  /**
   * Limite do vínculo automático em texto ("85%"), vindo do backend. Sem o campo (API antiga), nulo, e a tela
   * explica sem o número.
   */
  protected readonly limiteAutomatico = computed(() => {
    const limite = this.insights()?.autoApplyMinConfidence;
    return limite ? `${Math.round(limite * 100)}%` : null;
  });

  /**
   * A sugestão espera o clique porque a confiança ficou abaixo do limite. Sugestão de confiança alta que
   * continua pendente (reunião processada antes do vínculo automático, ou vínculo que falhou) não leva a frase.
   */
  protected abaixoDoLimite(sugestao: MeetingSuggestedLinkDto): boolean {
    const limite = this.insights()?.autoApplyMinConfidence;
    return !!limite && (sugestao.confidence ?? 0) < limite;
  }

  /** "Alta", "Média" ou "Baixa", para não mostrar número cru ao usuário. */
  protected confianca(valor: number): string {
    return rotuloDaConfianca(valor);
  }
}
